// ABOUTME: Bridges alpha-finder's API vocabulary to the browser-inference DataSources contract
// ABOUTME: Each fetcher translates a `{ symbol, mlScore, horizon, modelId }` response to the package shape

import type { DataSources } from '@boundarylab/browser-inference/react';
import { apiClient } from '../api/client';
import { fetchPlatformModelOnnxUrl } from '../api/platform-models';

interface AlphaFeaturesResponse {
  generatedAt: string;
  horizon: string;
  featureColumns: string[];
  symbols: Record<string, number[]>;
}
interface AlphaModelUrlResponse {
  modelId: string;
  url: string;
}
interface AlphaFeaturesInfoResponse {
  horizons: Record<
    string,
    { featureColumns: string[]; featureCount: number; modelType: string; modelId?: string }
  >;
}
interface AlphaSnapshotResponse {
  calculationDate: string | null;
  scores: Array<{ symbol: string; mlScore: number | null; calculatedAt: string }>;
}
interface AlphaBaselineResponse {
  histogram: number[];
  mean: number;
  stddev: number;
}
interface AlphaPredictionRow {
  symbol: string;
  predictedAt: string;
  predictedReturn: number;
  actualReturn: number | null;
}

export const inferenceDataSources: DataSources = {
  featuresLatest: async (windowKey) => {
    const res = await apiClient.get<{ data: AlphaFeaturesResponse }>(
      `/features/latest?horizon=${encodeURIComponent(windowKey)}`
    );
    const d = res.data.data;
    return { entities: d.symbols, columns: d.featureColumns, generatedAt: d.generatedAt };
  },
  modelUrl: async (windowKey, modelKey) => {
    if (modelKey) {
      const onnx = await fetchPlatformModelOnnxUrl(modelKey);
      return { modelKey: onnx.modelId, url: onnx.url };
    }
    const res = await apiClient.get<{ data: AlphaModelUrlResponse }>(
      `/features/model-url/${encodeURIComponent(windowKey)}`
    );
    return { modelKey: res.data.data.modelId, url: res.data.data.url };
  },
  featuresInfo: async () => {
    const res = await apiClient.get<{ data: AlphaFeaturesInfoResponse }>('/features/info');
    const windows: Record<string, { columns: string[]; columnCount: number; modelKey?: string }> =
      {};
    for (const [k, v] of Object.entries(res.data.data.horizons)) {
      windows[k] = { columns: v.featureColumns, columnCount: v.featureCount, modelKey: v.modelId };
    }
    return { windows };
  },
  snapshot: async (windowKey, entityIds) => {
    const qs = new URLSearchParams({ horizon: windowKey });
    if (entityIds.length > 0) qs.set('symbols', entityIds.join(','));
    const res = await apiClient.get<{ data: AlphaSnapshotResponse }>(
      `/alpha-scores/snapshot?${qs.toString()}`
    );
    const entities: Record<string, { value: number | null; capturedAt: string } | null> = {};
    for (const s of res.data.data.scores) {
      entities[s.symbol] = { value: s.mlScore, capturedAt: s.calculatedAt };
    }
    return { entities, capturedAt: res.data.data.calculationDate };
  },
  distributionBaseline: async (windowKey) => {
    const res = await apiClient.get<{ data: AlphaBaselineResponse }>(
      `/monitoring/score-baseline?horizon=${encodeURIComponent(windowKey)}`
    );
    return res.data.data;
  },
  predictionsLog: async (windowKey, windowDays, entityIds) => {
    const qs = new URLSearchParams({ horizon: windowKey, days: String(windowDays) });
    if (entityIds && entityIds.length > 0) qs.set('symbols', entityIds.join(','));
    const res = await apiClient.get<{ data: { rows: AlphaPredictionRow[] } }>(
      `/predictions/log?${qs.toString()}`
    );
    return res.data.data.rows.map((r) => ({
      entityId: r.symbol,
      predictedAt: r.predictedAt,
      predicted: r.predictedReturn,
      actual: r.actualReturn,
    }));
  },
};
